/**
 * Email / SMS signup client → Cloudflare Worker (Omnisend + Redo).
 * Falls back to Next /api/subscribe if worker URL unset.
 * Exposes window.TBP.subscribe + window.TBP.applySubscriberDiscount for CRO popup.
 */
(function () {
  'use strict';

  /* Shopify storefront → API-only Next on api.* (not the public apex) */
  var DEFAULT_API = 'https://api.thebuttresspillow.com/api/subscribe';
  var LEGACY_API = 'https://thebuttresspillow.com/api/subscribe';
  var DEFAULT_CODE = 'CHEEKY10';
  var STORAGE_KEY = 'tbp-subscriber-discount';

  function cfg() {
    var c = window.TBP_SUBSCRIBE || {};
    var api = (c.apiUrl || DEFAULT_API).replace(/\/$/, '');
    return {
      apiUrl: api || LEGACY_API,
      discountCode: (c.discountCode || DEFAULT_CODE).trim() || DEFAULT_CODE,
    };
  }

  function makeId(prefix) {
    try {
      if (window.crypto && crypto.randomUUID) return prefix + '_' + crypto.randomUUID();
    } catch (e) {}
    return prefix + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9);
  }

  function getExternalId() {
    try {
      var existing = localStorage.getItem('tbp_external_id');
      if (existing) return existing;
      var id = makeId('ext');
      localStorage.setItem('tbp_external_id', id);
      return id;
    } catch (e) {
      return makeId('ext');
    }
  }

  function persistLocal(email, code, phone) {
    try {
      localStorage.setItem('user-subscribed', 'true');
      localStorage.setItem('user-email', email);
      if (phone) localStorage.setItem('user-phone', phone);
      localStorage.setItem(STORAGE_KEY, code);
      sessionStorage.setItem(STORAGE_KEY, code);
    } catch (e) {}
  }

  function identifyOmnisend(email, phone) {
    try {
      window.omnisend = window.omnisend || [];
      var payload = { email: email };
      if (phone) payload.phone = phone;
      window.omnisend.push(['identify', payload]);
    } catch (e) {}
  }

  function setStatus(form, type, html) {
    var el = form.querySelector('[data-tbp-subscribe-status]');
    if (!el) return;
    el.hidden = !html;
    el.classList.toggle('is-error', type === 'error');
    el.classList.toggle('is-success', type === 'success');
    el.innerHTML = html || '';
  }

  function setBusy(form, busy) {
    form.classList.toggle('is-loading', busy);
    var btn = form.querySelector('[type="submit"]');
    if (btn) {
      btn.disabled = busy;
      if (busy) {
        btn.dataset.tbpLabel = btn.dataset.tbpLabel || btn.textContent;
        btn.textContent = '…';
      } else if (btn.dataset.tbpLabel) {
        btn.textContent = btn.dataset.tbpLabel;
      }
    }
    var input = form.querySelector('input[type="email"]');
    if (input) input.disabled = busy;
  }

  function applyDiscount(code) {
    var c = (code || cfg().discountCode || DEFAULT_CODE).trim();
    if (!c) return Promise.resolve();

    try {
      localStorage.setItem(STORAGE_KEY, c);
      sessionStorage.setItem(STORAGE_KEY, c);
    } catch (e) {}

    // Prefer cart helper (sets cookie + cart/update + refreshes drawer)
    if (window.TBP && typeof window.TBP.applyDiscountCode === 'function') {
      return window.TBP.applyDiscountCode(c, { open: false, forcePersist: true }).catch(function () {
        return applyDiscountFallback(c);
      });
    }
    return applyDiscountFallback(c);
  }

  function applyDiscountFallback(code) {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    var tasks = [
      fetch(root + 'discount/' + encodeURIComponent(code), {
        credentials: 'same-origin',
        redirect: 'manual',
      }).catch(function () {}),
      fetch(root + 'cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      }).catch(function () {}),
    ];

    return Promise.all(tasks).then(function () {
      if (window.TBP && typeof window.TBP.refreshCartDrawer === 'function') {
        return window.TBP.refreshCartDrawer({ open: false });
      }
      try {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true })
        );
      } catch (e) {}
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        try {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'tbp-subscribe' });
        } catch (e) {}
      }
    });
  }

  function successHtml(code, applied) {
    return (
      '<strong>You’re in!</strong> Your code is ' +
      '<span class="tbp-subscribe__code">' +
      code +
      '</span>' +
      (applied
        ? ' — it’s applied to your cart. Add a pillow and check out.'
        : ' — use it at checkout for 10% off.')
    );
  }

  /**
   * Shared subscribe call for inline forms + CRO popup.
   * @param {object} payload
   * @returns {Promise<{discountCode:string, metaEventId?:string, message?:string}>}
   */
  function subscribe(payload) {
    var conf = cfg();
    var metaEventId = payload.metaEventId || makeId('lead');
    var body = {
      email: payload.email,
      firstName: payload.firstName || String(payload.email || '').split('@')[0],
      phone: payload.phone,
      smsConsent: payload.smsConsent,
      step: payload.step != null ? payload.step : 1,
      signupSource: payload.signupSource || 'Shopify Signup',
      tags: payload.tags || [],
      _hp: payload._hp || '',
      _ts: payload._ts != null ? payload._ts : 5000,
      metaEventId: metaEventId,
      metaEventTime: payload.metaEventTime || Math.floor(Date.now() / 1000),
      metaEventSourceUrl: payload.metaEventSourceUrl || window.location.href,
      metaLeadContentName: payload.metaLeadContentName || payload.signupSource || 'Shopify Signup',
      metaExternalId: payload.metaExternalId || getExternalId(),
    };

    function postTo(url) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
        mode: 'cors',
        credentials: 'omit',
      }).then(function (res) {
        return res.json().then(function (json) {
          return { ok: res.ok, status: res.status, json: json || {} };
        });
      });
    }

    return postTo(conf.apiUrl)
      .catch(function () {
        // Fall back to apex Next only while api.* is not DNS'd yet
        if (conf.apiUrl.indexOf(LEGACY_API) >= 0 || conf.apiUrl === LEGACY_API) {
          throw new Error('Something went wrong. Please try again.');
        }
        return postTo(LEGACY_API);
      })
      .then(function (result) {
        if (!result.ok) {
          throw new Error(result.json.message || 'Something went wrong. Please try again.');
        }
        var code = (result.json.discountCode || conf.discountCode || DEFAULT_CODE).trim();
        persistLocal(payload.email, code, payload.phone);
        identifyOmnisend(payload.email, payload.phone);
        return {
          discountCode: code,
          metaEventId: result.json.metaEventId || metaEventId,
          message: result.json.message,
        };
      });
  }

  function onSubmit(e) {
    var form = e.target;
    if (!form || !form.hasAttribute('data-tbp-subscribe')) return;
    e.preventDefault();

    var emailInput = form.querySelector('input[type="email"]');
    var email = emailInput && emailInput.value ? emailInput.value.trim() : '';
    var hp = form.querySelector('[data-tbp-hp]');
    if (hp && hp.value) return;

    if (!email || email.indexOf('@') < 1) {
      setStatus(form, 'error', 'Please enter a valid email.');
      return;
    }

    var started = Number(form.dataset.tbpStarted || Date.now());
    var source =
      form.getAttribute('data-signup-source') ||
      form.dataset.signupSource ||
      'Shopify Final CTA';
    var tagsRaw = form.getAttribute('data-tags') || form.dataset.tags || '';
    var tags = tagsRaw
      .split(',')
      .map(function (t) {
        return t.trim();
      })
      .filter(Boolean);

    setBusy(form, true);
    setStatus(form, '', '');

    subscribe({
      email: email,
      firstName: email.split('@')[0],
      step: 1,
      signupSource: source,
      tags: tags,
      _ts: Date.now() - started,
      metaLeadContentName: source,
    })
      .then(function (result) {
        form.classList.add('is-done');
        return applyDiscount(result.discountCode).then(
          function () {
            setStatus(form, 'success', successHtml(result.discountCode, true));
            if (emailInput) emailInput.value = '';
          },
          function () {
            setStatus(form, 'success', successHtml(result.discountCode, false));
            if (emailInput) emailInput.value = '';
          }
        );
      })
      .catch(function (err) {
        setStatus(form, 'error', (err && err.message) || 'Something went wrong. Please try again.');
      })
      .finally(function () {
        setBusy(form, false);
      });
  }

  function enhanceCheckoutLinks(code) {
    if (!code) return;
    var checkoutBtn = document.getElementById('CartDrawer-Checkout');
    if (checkoutBtn && checkoutBtn.form) {
      checkoutBtn.addEventListener(
        'click',
        function () {
          applyDiscount(code);
        },
        { once: true }
      );
    }
  }

  function init() {
    document.querySelectorAll('form[data-tbp-subscribe]').forEach(function (form) {
      if (form.dataset.tbpBound) return;
      form.dataset.tbpBound = '1';
      form.dataset.tbpStarted = String(Date.now());
      form.addEventListener('submit', onSubmit);
    });

    try {
      var code = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
      if (code) enhanceCheckoutLinks(code);
    } catch (e) {}
  }

  window.TBP = window.TBP || {};
  window.TBP.subscribe = subscribe;
  window.TBP.applySubscriberDiscount = function (code) {
    var c = (code || cfg().discountCode || DEFAULT_CODE).trim();
    var email = '';
    try {
      email = localStorage.getItem('user-email') || '';
    } catch (e) {}
    persistLocal(email, c);
    return applyDiscount(c);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
